import { useContext } from "react";
import { NavLink } from "react-router-dom";
import { getSessionStore, removeSessionStore } from "app/utils/sessionStore";
import { ContactProfileDetailsContext } from "../macrocomponents/ContactLayout";
import {
  getTextColorBasedOnBG,
  getThemedPrimarySecondaryFromSource,
  hexToRgb,
  isBgDark,
} from "../../utils/utils";
import pendingSVG from "../../assets/svg/PendingDark.svg";
import pendingLightSVG from "../../assets/svg/PendingLight.svg";
import educationLightSVG from "../../assets/EducationLight.svg";
import educationDarkSVG from "../../assets/EducationDark.svg";
import proposalsSVG from "../../assets/svg/ProposalsLight.svg";
import proposalsDarkSVG from "../../assets/svg/ProposalsDark.svg";
import plansSVG from "../../assets/svg/PlansDark.svg";
import plansLightSVG from "../../assets/svg/PlansLight.svg";
import { Tooltip } from "primereact/tooltip";
import { THEME } from "app/utils/types";
import useBgBasedTextColor from "app/hooks/useBgBasedTextColor";
import ClientDashboardIcon from "../../assets/IconComponent/ClientDashboardIcon";
import { ReactComponent as AdChat } from "../../assets/svg/Ad-Chat.svg";

const NotificationSvg = ({
  width = "22",
  height = "24",
  fill = "#00132E",
}: Readonly<{
  width?: number | string;
  height?: number | string;
  fill?: string;
}>) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 22 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M21.0577 20.0676C21.3956 20.0649 21.6437 19.9785 21.7964 19.8109C21.9491 19.6433 22.0165 19.382 21.9962 19.0357C21.9007 17.3975 21.164 16.1469 19.8056 15.3199C19.3221 15.0253 19.1057 14.6364 19.1057 14.0582V5.96678C19.1057 4.99447 18.6018 4.09615 17.7836 3.60967L12.3227 0.364709C11.5045 -0.121773 10.4961 -0.121773 9.67789 0.364709L4.21702 3.60967C3.39881 4.09615 2.8949 4.99447 2.8949 5.96678V13.6366C2.89554 13.7636 2.89618 13.8919 2.89808 14.0203V14.0235C2.89808 14.0792 2.89681 14.1257 2.89427 14.1689C2.865 14.6881 2.64486 15.0463 2.18104 15.3311C0.805482 16.1757 0.0725309 17.4557 0.00127171 19.1345C-0.0146344 19.5235 0.104343 19.7814 0.365839 19.9216C0.530626 20.0093 0.762854 20.061 1.00399 20.0623C2.06206 20.0708 21.0577 20.0676 21.0577 20.0676ZM1.76557 18.3731L1.91509 18.009C2.14286 17.4544 2.4928 17.0537 2.98461 16.7833C3.67112 16.4055 4.11585 15.9027 4.34299 15.2453C4.47342 14.8662 4.53259 14.4432 4.52368 13.9522C4.50523 12.9458 4.51032 9.12073 4.51541 6.92927C4.51732 5.95958 5.02123 5.06453 5.83753 4.57936L9.67789 2.29689C10.4961 1.81041 11.5045 1.81041 12.3234 2.29689L16.1625 4.5787C16.9788 5.06388 17.4827 5.95958 17.4846 6.92993C17.4884 8.99044 17.4935 12.5078 17.4878 13.8664L17.4865 14.1925C17.4852 14.5788 17.5399 14.9343 17.6494 15.2499C17.8581 15.8555 18.2646 16.3296 18.8939 16.7002C19.5352 17.076 19.9131 17.516 20.0493 18.0437L20.1339 18.3731H1.76557Z"
      fill={fill}
    />
    <path
      d="M11.892 24H10.108C9.03019 24 8.02557 23.4029 7.48603 22.4424L5.9406 19.6872C5.73 19.3113 5.85471 18.8307 6.21991 18.614C6.58511 18.3973 7.05212 18.5256 7.26271 18.9015L8.80815 21.6567C9.07537 22.1327 9.57291 22.4286 10.1074 22.4286H11.8914C12.4258 22.4286 12.9234 22.1327 13.1906 21.6567L14.736 18.9015C14.9466 18.5256 15.4143 18.3966 15.7788 18.614C16.144 18.8307 16.2694 19.3113 16.0581 19.6872L14.5127 22.4424C13.9738 23.4029 12.9692 24 11.8907 24H11.892Z"
      fill={fill}
    />
  </svg>
);

function ContactLeftPanelMenuItem({
  name,
  icon,
  directTo,
}: Readonly<{
  name: string;
  icon: string;
  directTo: string;
}>) {
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor = "" } = getThemedPrimarySecondaryFromSource(branding ?? {}, "", "");
  const { isOpen } = useContext(ContactProfileDetailsContext);
  const globalTheme = getSessionStore("globalTheme");
  const color = useBgBasedTextColor();
  document.documentElement.style.setProperty("--cta", primaryColor ? hexToRgb(color) : "0,36,70");

  const showItemIcon = (isActive: boolean) => {
    const chosenIcon = (isActive && isBgDark(color)) || (!isActive && globalTheme === THEME.DARK);
    if (name === "Plans") {
      return (
        <img
          src={chosenIcon ? plansLightSVG : plansSVG}
          data-pc-section="image"
          className="w-[1rem] h-[1rem]"
          alt="plans icon"
        />
      );
    }
    if (name === "Proposals") {
      return (
        <img
          src={chosenIcon ? proposalsSVG : proposalsDarkSVG}
          data-pc-section="image"
          className="w-[1rem] h-[1rem]"
          alt="plans icon"
        />
      );
    }
    if (name === "Pending") {
      return (
        <img
          src={chosenIcon ? pendingLightSVG : pendingSVG}
          data-pc-section="image"
          className="w-[1.4rem] h-[1.4rem]"
          alt="pending icon"
        />
      );
    }
    if (name === "IAPP Academy") {
      return (
        <img
          src={chosenIcon ? educationLightSVG : educationDarkSVG}
          data-pc-section="image"
          className="w-[1rem] h-[1rem]"
          alt="IAPP Academy"
        />
      );
    }
    if (name === "Dashboard") {
      return (
        <ClientDashboardIcon
          fill={
            isActive
              ? getTextColorBasedOnBG(color)
              : !isActive && globalTheme === THEME.DARK
              ? "#ffffff"
              : "#000000"
          }
        />
      );
    }
    if (name === "Chat") {
      return <AdChat style={{ width: "1.2rem", height: "1.2rem" }} />;
    }
    if (directTo === "/notification") {
      const fill = isActive
        ? getTextColorBasedOnBG(color)
        : globalTheme === THEME.DARK
        ? "#ffffff"
        : "#00132E";

      return <NotificationSvg width="1.2rem" height="1.2rem" fill={fill} />;
    }
    return (
      <i
        className={`pi ${icon}`}
        style={{
          fontSize: "1rem",
          ...(isActive ? { color: getTextColorBasedOnBG(color) } : {}),
        }}
      />
    );
  };

  return (
    <li className="my-2 relative group w-full flex justify-center items-center">
      <NavLink
        to={directTo}
        onClick={() => removeSessionStore("dashboardActiveTab")}
        className={
          ({ isActive }) =>
            `custom-target-icon flex  ${
              isOpen
                ? "w-full h-full items-start justify-start pl-3"
                : "w-[46px] h-[48px] items-center justify-center"
            } py-3 rounded-md
        ${isActive ? "text-white" : "hover:bg-gray-100 dark:hover:!bg-gray-800"}`
          // eslint-disable-next-line react/jsx-curly-newline
        }
        style={({ isActive }) => (isActive ? { backgroundColor: color } : {})}
        data-pr-tooltip={name}
      >
        {({ isActive }) => (
          <div className="flex gap-2 items-center justify-start !bg-transparent">
            {showItemIcon(isActive)}
            {isOpen && (
              <span
                className="text-sm text-left whitespace-nowrap transition-all overflow-hidden"
                style={isActive ? { color: getTextColorBasedOnBG(color) } : {}}
              >
                {name}
              </span>
            )}
          </div>
        )}
      </NavLink>
      {!isOpen && <Tooltip target=".custom-target-icon" className="tooltip-wrapper" />}
    </li>
  );
}

export default ContactLeftPanelMenuItem;
